import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Upload, Loader2, X, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { useData } from "@/context/DataContext";
import { api, formatApiError } from "@/lib/api";
import Seo from "@/components/site/Seo";
import { PageHero } from "@/components/site/Layout";
import { Reveal } from "@/components/site/Reveal";
import { WhatsAppButton } from "@/components/site/WhatsButton";
import { searchGuides, track } from "@/lib/track";

const AREE = ["Prato", "Albero", "Siepe", "Pianta ornamentale", "Impianto di irrigazione", "Ceppo", "Terreno", "Altro"];
const SINTOMI = ["prato giallo", "prato secco", "zone senza erba", "muschio", "foglie macchiate", "foglie bucate", "foglie arricciate", "caduta delle foglie", "rami secchi", "pianta inclinata", "presenza di insetti", "ragnatele sulle piante", "muffa", "funghi", "irrigatore fermo", "perdita d'acqua", "terreno troppo bagnato", "ceppo da eliminare"];
const PERICOLO = ["rami secchi", "pianta inclinata"];
const AREA_SERVIZIO = { "Prato": "Manutenzione giardini", "Albero": "Potature", "Siepe": "Manutenzione giardini", "Pianta ornamentale": "Trattamenti fitosanitari", "Impianto di irrigazione": "Costruzione e rifacimento giardini", "Ceppo": "Abbattimenti", "Terreno": "Costruzione e rifacimento giardini", "Altro": "Sopralluoghi e preventivi gratuiti" };
const MAX = 8 * 1024 * 1024;

export default function Diagnostica() {
  const { guides, guideCategories } = useData();
  const [area, setArea] = useState("");
  const [sintomi, setSintomi] = useState([]);
  const [descr, setDescr] = useState("");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { track("diagnostica_open", "", "/diagnostica-giardino"); }, []);

  const toggle = (s) => setSintomi((p) => p.includes(s) ? p.filter((x) => x !== s) : [...p, s]);

  const onFiles = async (list) => {
    setUploading(true);
    try {
      for (const file of Array.from(list)) {
        if (!file.type.startsWith("image/")) { toast.error("Solo immagini"); continue; }
        if (file.size > MAX) { toast.error("File troppo pesante (max 8MB)"); continue; }
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setFiles((p) => [...p, { name: file.name, url: data.url }]);
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore upload"); }
    finally { setUploading(false); }
  };

  const analizza = () => {
    const query = `${area} ${sintomi.join(" ")} ${descr}`;
    const g = searchGuides(guides, query, guideCategories).slice(0, 4);
    const urgente = sintomi.some((s) => PERICOLO.includes(s));
    setResult({ guide: g, servizio: AREA_SERVIZIO[area] || "Sopralluoghi e preventivi gratuiti", urgente });
    track("diagnostica_submit", area, "/diagnostica-giardino");
    setTimeout(() => document.getElementById("diag-result")?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  return (
    <>
      <Seo title="Diagnostica del giardino" description="Seleziona i sintomi del tuo giardino e scopri le guide e i servizi che potrebbero esserti utili. La valutazione online è indicativa." path="/diagnostica-giardino" />
      <PageHero
        overline="Strumento gratuito"
        title="Quale problema presenta il tuo giardino?"
        subtitle="Seleziona i sintomi oppure carica alcune fotografie. Ti mostreremo le guide e i servizi che potrebbero essere utili. La valutazione online è indicativa e non sostituisce un sopralluogo professionale."
        breadcrumb={<><Link to="/" className="hover:text-av-lime">Home</Link> / <span className="text-av-white">Diagnostica del giardino</span></>}
      />
      <section className="bg-av-black py-14 sm:py-20">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal>
            <h2 className="font-display text-2xl text-av-white mb-4">1. Qual è l'area interessata?</h2>
            <div className="flex flex-wrap gap-2.5 mb-10">
              {AREE.map((a) => (
                <button key={a} onClick={() => setArea(a)} data-testid={`diag-area-${a.toLowerCase().replace(/\s+/g,'-')}`}
                        className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${area === a ? "bg-av-bright text-av-black" : "border border-white/20 text-av-white/80 hover:border-av-lime"}`}>{a}</button>
              ))}
            </div>

            <h2 className="font-display text-2xl text-av-white mb-4">2. Quali sintomi noti?</h2>
            <div className="flex flex-wrap gap-2.5 mb-10">
              {SINTOMI.map((s) => (
                <button key={s} onClick={() => toggle(s)} data-testid={`diag-sintomo-${s.replace(/\s+/g,'-').replace(/'/g,'')}`}
                        className={`rounded-full px-4 py-2 text-sm transition-colors ${sintomi.includes(s) ? "bg-av-green text-white" : "border border-white/20 text-av-white/80 hover:border-av-lime"}`} style={sintomi.includes(s) ? { backgroundColor: "var(--av-green)" } : {}}>{s}</button>
              ))}
            </div>

            <h2 className="font-display text-2xl text-av-white mb-4">3. Aggiungi dettagli (facoltativo)</h2>
            <textarea data-testid="diag-descr" rows={3} value={descr} onChange={(e) => setDescr(e.target.value)} placeholder="Da quanto tempo? Dimensione dell'area? Comune?"
                      className="w-full rounded-xl bg-av-green-dark/40 border border-white/15 px-4 py-3 text-av-white placeholder:text-av-gray/60 focus:border-av-lime outline-none resize-none mb-6" style={{ backgroundColor: "rgba(22,56,29,0.4)" }} />

            <label data-testid="diag-upload" className="flex items-center justify-center gap-3 rounded-xl border border-dashed border-white/25 p-5 cursor-pointer hover:border-av-lime/60 transition-colors text-av-gray mb-3">
              {uploading ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
              <span className="text-sm">{uploading ? "Caricamento..." : "Carica fotografie (facoltativo, max 8MB)"}</span>
              <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => onFiles(e.target.files)} />
            </label>
            {files.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {files.map((f, i) => (
                  <span key={i} className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-xs text-av-white">{f.name}
                    <button onClick={() => setFiles((p) => p.filter((_, idx) => idx !== i))} className="text-av-gray hover:text-red-400"><X size={13} /></button>
                  </span>
                ))}
              </div>
            )}
            <p className="text-xs text-av-gray/60 mb-8">Le fotografie vengono usate solo per aiutarti a trovare le guide pertinenti e non vengono pubblicate.</p>

            <button onClick={analizza} disabled={!area && sintomi.length === 0} data-testid="diag-submit"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-av-bright text-av-black px-8 py-4 text-sm font-semibold hover:bg-av-lime transition-colors disabled:opacity-50">
              Mostra guide e servizi consigliati
            </button>
          </Reveal>

          {result && (
            <div id="diag-result" className="mt-14" data-testid="diag-result">
              <h2 className="font-display text-3xl text-av-white mb-2">Risultato indicativo</h2>
              <p className="text-sm text-av-gray mb-6">Il sintomo potrebbe essere collegato a diverse cause. Per una conferma è consigliato un sopralluogo.</p>

              {result.urgente && (
                <div className="mb-6 flex items-start gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4">
                  <AlertTriangle size={20} className="text-amber-400 shrink-0 mt-0.5" />
                  <p className="text-amber-200 text-sm">Attenzione: i sintomi selezionati potrebbero indicare una situazione da valutare con priorità. Per alberi inclinati o rami instabili non intervenire da solo e richiedi un sopralluogo.</p>
                </div>
              )}

              {result.guide.length > 0 ? (
                <div className="space-y-3 mb-8">
                  {result.guide.map((g) => (
                    <Link key={g.id} to={`/guide/${g.slug}`} className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 p-5 hover:border-av-lime/50 transition-colors">
                      <span className="text-av-white">{g.domanda || g.titolo}</span>
                      <ArrowUpRight size={16} className="text-av-lime shrink-0" />
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-av-gray mb-8">Non abbiamo trovato una guida specifica. Descrivici il problema: valuteremo insieme la soluzione più adatta.</p>
              )}

              <div className="rounded-2xl border border-av-lime/30 bg-av-green-dark/40 p-6 text-center" style={{ backgroundColor: "rgba(22,56,29,0.4)" }}>
                <p className="overline text-av-lime mb-2">Servizio consigliato</p>
                <p className="font-display text-2xl text-av-white mb-5">{result.servizio}</p>
                <div className="flex flex-wrap justify-center gap-3">
                  <Link to="/contatti" className="inline-flex items-center gap-2 rounded-full bg-av-bright text-av-black px-6 py-3 text-sm font-semibold hover:bg-av-lime transition-colors">Richiedi un sopralluogo</Link>
                  <WhatsAppButton variant="outline" />
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
